import {useQuery} from '@tanstack/react-query';
import * as I from 'immutable';
import React from 'react';
import {useLocation} from 'react-router-dom';

import {PushNotificationFn} from 'app/components/Notification';
import api from 'app/modules/Remote/api';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {ApiShareLink} from 'app/modules/Remote/ShareLink';

import cs from '../PropertyOverview.module.scss';
import {useSyncTableStateToUrl} from '../tableUrlState';
import PortfolioShareLinksControls from './PortfolioShareLinksControls';
import PortfolioShareLinksTable, {ShareLinksStatus} from './PortfolioShareLinksTable';
import ShareLinkDeleteDialog from './ShareLinkDeleteDialog';
import {searchWithTableState, tableStateFromSearch} from './shareLinksTableState';
import {usePortfolioShareLinksTable} from './usePortfolioShareLinksTable';

const NO_SHARE_LINKS: ApiShareLink[] = [];

export function usePortfolioShareLinks(organizationId: string, featureCollectionId: number) {
  return useQuery({
    queryKey: ['shareLinks', organizationId, featureCollectionId],
    queryFn: async () => {
      const response = await api
        .shareLinks(organizationId)
        .getList({featureCollectionId, perPage: 100});
      return response.get('data').toJS() as ApiShareLink[];
    },
  });
}

export interface Props {
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  selectedProject: I.ImmutableOf<ApiProject>;
  selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;
  pushNotification: PushNotificationFn;
  /** Rendered first in the controls row. */
  viewToggle?: React.ReactNode;
}

/**
 * Every live share link to one of the portfolio's properties, with selection
 * for bulk deletion. Filters and sort live in the query string so the view is
 * bookmarkable.
 */
const PortfolioShareLinks: React.FunctionComponent<Props> = ({
  organization,
  profile,
  selectedProject,
  selectedFeatureCollection,
  pushNotification,
  viewToggle,
}) => {
  const organizationId = organization.get('id');
  const {data: shareLinks, status: queryStatus} = usePortfolioShareLinks(
    organizationId,
    selectedFeatureCollection.get('id')
  );
  const status: ShareLinksStatus =
    queryStatus === 'pending' ? 'loading' : queryStatus === 'error' ? 'error' : 'done';

  return (
    <PortfolioShareLinksView
      organizationId={organizationId}
      projectId={selectedProject.get('id')}
      role={profile.get('role')}
      shareLinks={shareLinks ?? NO_SHARE_LINKS}
      status={status}
      pushNotification={pushNotification}
      viewToggle={viewToggle}
    />
  );
};

export const PortfolioShareLinksView: React.FunctionComponent<{
  organizationId: string;
  projectId: string;
  role: ApiOrganizationUser['role'];
  shareLinks: ApiShareLink[];
  status: ShareLinksStatus;
  pushNotification: PushNotificationFn;
  viewToggle?: React.ReactNode;
}> = ({organizationId, projectId, role, shareLinks, status, pushNotification, viewToggle}) => {
  const location = useLocation();

  // Deleting is the only action on a selection, and readonly users may not
  // delete anything, so they get no selection at all.
  const allowSelection = role !== 'readonly';

  const [initialTableState] = React.useState(() => tableStateFromSearch(location.search));
  const {table} = usePortfolioShareLinksTable({
    shareLinks,
    organizationId,
    projectId,
    allowSelection,
    initialFilters: initialTableState.filters,
    initialSortBy: initialTableState.sortBy,
  });

  useSyncTableStateToUrl(table, searchWithTableState);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const selectedShareLinks = React.useMemo(
    () => table.selectedFlatRows.map((row) => row.original),
    [table.selectedFlatRows]
  );

  return (
    <>
      <div className={cs.tableControls}>
        {viewToggle}
        <PortfolioShareLinksControls table={table} shareLinks={shareLinks} />
      </div>
      <PortfolioShareLinksTable
        table={table}
        status={status}
        hasShareLinks={shareLinks.length > 0}
        allowSelection={allowSelection}
        onDeleteSelected={() => setIsDeleteDialogOpen(true)}
      />
      <ShareLinkDeleteDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        organizationId={organizationId}
        shareLinks={selectedShareLinks}
        onDeleted={({deleted, skipped}) => {
          const deletedMessage = `Deleted ${deleted.length === 1 ? '1 share link' : `${deleted.length} share links`}.`;
          // The server re-checks each link: one selected here may have been
          // deleted by someone else, or belong to another user, in the meantime.
          const skippedMessage = skipped.length
            ? ` ${skipped.length === 1 ? '1 link' : `${skipped.length} links`} could not be deleted.`
            : '';
          pushNotification({message: deletedMessage + skippedMessage});
          table.toggleAllRowsSelected(false);
        }}
        onError={(error) =>
          pushNotification({
            message:
              error.message || 'Something went wrong deleting share links. Please try again.',
          })
        }
      />
    </>
  );
};

export default PortfolioShareLinks;
